/**
 * The legs, as controls rather than a readout — ROADMAP 5.25.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A TEMPLATE IS A STARTING POINT, NOT A VERDICT
 *
 * The confirmation used to show four resolved legs and offer one answer:
 * place them or cancel. But the strike a template picks is an ATM offset,
 * and the strike a trader wants is the one with the open interest, or the
 * one just past the level they are watching. Closing the modal to edit the
 * saved strategy, or firing something slightly wrong, were the only options.
 *
 * So the strike and the size are editable HERE, for this placement only.
 * The saved strategy is untouched — 5.19 owns that, and an edit made
 * seconds before an order is almost never one you want kept.
 *
 * THE STRIKE IS CHOSEN FROM WHAT IS LISTED, NEVER TYPED.
 *
 * A typed strike can be one the exchange does not list, and the resolver
 * would refuse it at the worst possible moment. The picker offers the
 * chain's own strikes with their prices beside them, so the only reachable
 * answers are the ones that can actually be traded.
 *
 * EVERY EDIT RECOMPUTES EVERYTHING. The payoff, the net premium and the
 * margin are all derived from the legs this component returns — change a
 * strike and all three move together, because none of them is stored.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Minus, Plus } from 'lucide-react'
import { expiryCode, type OptionType, type ProtectionSpec, type ResolvedLeg } from '@apna/shared'
import { cn } from '@/lib/utils'
import { ProtectionInput } from '@/components/ProtectionInput'
import type { PreviewChainRow } from '@/hooks/useStrategyPreviews'

export interface LegEdit {
  /** The strike this leg should trade, replacing the template's. */
  readonly strike: number
  /**
   * The expiry this leg should trade — ROADMAP 9.43.
   *
   * PER LEG, because that is what a calendar spread is: the same strike on
   * two dates. It used to be one date for the whole ticket, chosen in the
   * header, so a strategy whose legs carry different expiry kinds could be
   * built in the editor and never fired.
   *
   * A real listed DATE rather than a kind. The kind is what the template
   * stores and it is resolved to a date once, as the default; from there
   * the trader is choosing a contract, and a contract has a date.
   */
  readonly expiry: string
  /** Lots for this leg alone. */
  readonly lots: number
  /**
   * ROADMAP 7.5. What to protect this leg with, for THIS placement.
   *
   * A SPEC, NOT A PRICE. "40% of premium" has no price until the entry
   * fills, and a trigger worked out here would be wrong by the slippage —
   * on the one number whose whole job is to be a floor. The engine resolves
   * it against the fill and places the resting order.
   *
   * Null means none, and that is a choice rather than an omission:
   * protection is never invented for a trader who did not ask for it.
   */
  readonly sl: ProtectionSpec | null
  readonly tgt: ProtectionSpec | null
}

export interface LegEditorProps {
  legs: readonly ResolvedLeg[]
  /** Every expiry this underlying lists, for the per-leg selector. */
  expiries: readonly string[]
  /** The ladder for one expiry. Empty while that expiry is still loading. */
  rowsFor: (expiry: string) => readonly PreviewChainRow[]
  /**
   * This leg's expiry as it stands — the template's kind resolved to a
   * date, or the trader's override. Read back from the caller for the same
   * reason `protectionOf` is: every edit rewrites the whole `LegEdit`.
   */
  expiryOf: (index: number) => string
  lotSize: number
  atmStrike: number | null
  /** A leg was edited: seq, and the new strike and size. */
  onEdit: (index: number, edit: LegEdit) => void
  /** Prices for the dropdown, so a strike is chosen with its premium visible. */
  priceOf: (token: number) => number
  /**
   * This leg's protection as it stands.
   *
   * Read back from the caller rather than held here, because every edit
   * rewrites the whole `LegEdit` — and a strike change that did not carry
   * the stop forward would silently disarm it.
   */
  protectionOf: (index: number) => { sl: ProtectionSpec | null; tgt: ProtectionSpec | null }
}

export function LegEditor({
  legs,
  expiries,
  rowsFor,
  expiryOf,
  lotSize,
  atmStrike,
  onEdit,
  priceOf,
  protectionOf,
}: LegEditorProps) {
  return (
    <table className="w-full text-[12.5px]">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
          <th className="pb-1.5 font-semibold">Leg</th>
          <th className="pb-1.5 font-semibold">Strike</th>
          <th className="pb-1.5 font-semibold">Expiry</th>
          <th className="pb-1.5 text-right font-semibold">Lots</th>
          <th className="pb-1.5 text-right font-semibold">Qty</th>
          <th className="pb-1.5 text-right font-semibold">Price</th>
          {/* The mockup's own column, on real controls — ROADMAP 7.5. */}
          <th className="pb-1.5 pl-3 text-left font-semibold">SL / TGT</th>
        </tr>
      </thead>
      <tbody className="num">
        {legs.map((l, i) => (
          <tr key={`${l.optionType}-${i}`} className="border-t border-border" data-testid={`preview-leg-${i}`}>
            <td className="py-1.5">
              <span
                className={cn(
                  'rounded px-1.5 py-0.5 text-[11px] font-semibold',
                  l.side === 'BUY'
                    ? 'bg-success/10 text-success'
                    : 'bg-destructive/10 text-destructive',
                )}
              >
                {l.side} {l.optionType}
              </span>
            </td>

            <td className="py-1.5">
              <StrikePicker
                value={l.strike}
                optionType={l.optionType}
                rows={rowsFor(expiryOf(i))}
                atmStrike={atmStrike}
                priceOf={priceOf}
                onPick={(strike) =>
                  onEdit(i, { strike, lots: l.lots, expiry: expiryOf(i), ...protectionOf(i) })
                }
                testId={`leg-strike-${i}`}
              />
            </td>

            <td className="py-1.5">
              <ExpiryPicker
                value={expiryOf(i)}
                expiries={expiries}
                onPick={(next) =>
                  onEdit(i, { strike: l.strike, lots: l.lots, expiry: next, ...protectionOf(i) })
                }
                testId={`leg-expiry-${i}`}
              />
            </td>

            <td className="py-1.5">
              <LotStepper
                value={l.lots}
                onChange={(lots) =>
                  onEdit(i, { strike: l.strike, lots, expiry: expiryOf(i), ...protectionOf(i) })
                }
                testId={`leg-lots-${i}`}
              />
            </td>

            <td className="py-1.5 text-right text-[11.5px] text-muted-foreground">
              {l.lots * lotSize}
            </td>

            <td className="py-1.5 text-right">
              {/* The side that will actually be crossed. */}
              {(l.side === 'SELL' ? l.bid : l.ask)?.toFixed(2) ?? '—'}
            </td>
            <td className="py-1.5 pl-3">
              <span className="flex items-center gap-1">
                <ProtectionInput
                  label="SL"
                  tone="sl"
                  value={protectionOf(i).sl}
                  testId={`leg-sl-${i}`}
                  onChange={(sl) =>
                    onEdit(i, {
                      strike: l.strike,
                      lots: l.lots,
                      expiry: expiryOf(i),
                      sl,
                      tgt: protectionOf(i).tgt,
                    })
                  }
                />
                <ProtectionInput
                  label="TGT"
                  tone="tgt"
                  value={protectionOf(i).tgt}
                  testId={`leg-tgt-${i}`}
                  onChange={(tgt) =>
                    onEdit(i, {
                      strike: l.strike,
                      lots: l.lots,
                      expiry: expiryOf(i),
                      sl: protectionOf(i).sl,
                      tgt,
                    })
                  }
                />
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/**
 * Pick the expiry THIS leg trades — ROADMAP 9.43.
 *
 * A NATIVE SELECT, deliberately, where the strike beside it is a custom
 * panel. The strike list is long, wants centring on the current value and
 * carries a price per row; an expiry list is four to ten short dates with
 * nothing beside them, and the platform's own control is better at that
 * than anything built here — it is keyboard-navigable, it scrolls on a
 * phone, and it costs no outside-click handling.
 *
 * It shows every expiry the underlying lists rather than only the loaded
 * ones. An expiry whose ladder has not arrived yet renders its legs
 * unpriced and blocks the send, which is the same state as a slow network
 * and is handled where sending is decided — hiding the choice until the
 * fetch lands would make the control flicker as data arrived.
 */
function ExpiryPicker({
  value,
  expiries,
  onPick,
  testId,
}: {
  value: string
  expiries: readonly string[]
  onPick: (expiry: string) => void
  testId: string
}) {
  /*
   * A leg can hold an expiry that is no longer listed — the strategy was
   * opened before a roll, or the catalogue refreshed under it. Showing it
   * anyway keeps the control honest about what the leg actually says;
   * dropping it would silently repoint the leg at a date nobody chose.
   */
  const options = expiries.includes(value) ? expiries : [value, ...expiries]

  return (
    <select
      value={value}
      data-testid={testId}
      aria-label="Expiry for this leg"
      onChange={(e) => onPick(e.target.value)}
      className="num h-7 rounded-md border border-border bg-background px-1.5 text-[11.5px] text-foreground outline-none transition-colors hover:border-[var(--tr-line3)] focus-visible:border-primary"
    >
      {options.map((e) => (
        <option key={e} value={e}>
          {expiryCode(e) ?? e}
        </option>
      ))}
    </select>
  )
}

/**
 * Pick a strike from the ones this expiry actually lists.
 *
 * Opens CENTRED on the current strike rather than at the top of the ladder:
 * the choice is always "a few strikes either way", and scrolling to find
 * where you are before you can move is the thing a ladder picker gets wrong.
 */
function StrikePicker({
  value,
  optionType,
  rows,
  atmStrike,
  priceOf,
  onPick,
  testId,
}: {
  value: number
  optionType: OptionType
  rows: readonly PreviewChainRow[]
  atmStrike: number | null
  priceOf: (token: number) => number
  onPick: (strike: number) => void
  testId: string
}) {
  const [open, setOpen] = useState(false)
  const current = useRef<HTMLButtonElement | null>(null)
  const box = useRef<HTMLDivElement | null>(null)

  /** Only strikes that are listed on THIS side — a CE leg cannot take a put-only strike. */
  const options = useMemo(
    () =>
      rows
        .map((r) => ({ strike: r.strike, token: optionType === 'CE' ? r.ceToken : r.peToken }))
        .filter((o): o is { strike: number; token: number } => o.token !== null)
        .sort((a, b) => a.strike - b.strike),
    [rows, optionType],
  )

  /*
   * A PLAIN PANEL, not a Radix popover.
   *
   * This is a list and a click; the library's version brings a portal, a
   * focus scope and a dismissable layer, and the last of those cannot
   * mount under jsdom — so the only tests that could cover editing a
   * strike before placing an order were the ones that could not run. The
   * behaviour wanted here is two lines of it.
   */
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => {
      if (!box.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    // After the panel paints, put the current strike in the middle of it.
    // `scrollIntoView` is absent in jsdom and optional everywhere else.
    const id = window.requestAnimationFrame(() => current.current?.scrollIntoView?.({ block: 'center' }))
    return () => window.cancelAnimationFrame(id)
  }, [open])

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex h-7 items-center gap-1.5 rounded-md border px-2 transition-colors',
          open ? 'border-primary bg-primary/10' : 'border-border hover:border-[var(--tr-line3)]',
        )}
      >
        <span className="num text-[12.5px] font-semibold">{value.toLocaleString('en-IN')}</span>
        <ChevronDown size={12} className={cn('opacity-60 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 max-h-[260px] w-[172px] overflow-auto rounded-lg border border-border bg-popover p-1 shadow-lg">
          {options.length === 0 && (
            <p className="px-2 py-3 text-[11.5px] text-muted-foreground">
              No {optionType} listed in this expiry.
            </p>
          )}
          {options.map((o) => {
            const isCurrent = o.strike === value
            const price = priceOf(o.token)
            return (
              <button
                key={o.strike}
                ref={isCurrent ? current : null}
                type="button"
                data-testid={`${testId}-option-${o.strike}`}
                onClick={() => {
                  onPick(o.strike)
                  setOpen(false)
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left transition-colors',
                  isCurrent ? 'bg-primary/15 font-semibold' : 'hover:bg-muted',
                )}
              >
                <span className="num text-[12.5px]">{o.strike.toLocaleString('en-IN')}</span>
                {atmStrike !== null && o.strike === atmStrike && (
                  <span className="rounded bg-primary/15 px-1 text-[8.5px] font-bold tracking-wider text-primary">
                    ATM
                  </span>
                )}
                <span className="flex-1" />
                {/* The premium, because a strike is chosen BY its price as
                    often as by its distance. A dash where there is no quote. */}
                <span className="num text-[11px] text-muted-foreground">
                  {price > 0 ? price.toFixed(2) : '—'}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

/** Lots for one leg. The ratio a template carries is just its starting value. */
function LotStepper({
  value,
  onChange,
  testId,
}: {
  value: number
  onChange: (lots: number) => void
  testId: string
}) {
  return (
    <div className="ml-auto flex h-7 w-[84px] items-center justify-between rounded-md border border-border">
      <button
        type="button"
        aria-label="One lot fewer"
        data-testid={`${testId}-minus`}
        onClick={() => onChange(Math.max(1, value - 1))}
        className="grid h-full w-6 place-items-center text-muted-foreground hover:text-foreground"
      >
        <Minus size={11} />
      </button>
      <span className="num text-[12.5px] font-semibold" data-testid={testId}>
        {value}
      </span>
      <button
        type="button"
        aria-label="One lot more"
        data-testid={`${testId}-plus`}
        onClick={() => onChange(value + 1)}
        className="grid h-full w-6 place-items-center text-muted-foreground hover:text-foreground"
      >
        <Plus size={11} />
      </button>
    </div>
  )
}
